import { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  Skeleton,
  CircularProgress,
  Stack,
  Tooltip,
  MenuItem,
  Menu,
} from "@mui/material";
import HorizontalBarChart from "./HorizontalBarChart";
import styles from "./CustomChart.module.css";
import OrderOverviewTable from "../TableBackup";
import VerticalBarChart from "./VerticalBarChart";
import MultiLineChart from "./MultiLineChart";
import CustomComposedChart from "./ComposedChart";
import PieChart from "Samarth Module/components/SamarthAi/Graphs/PieChart";
import DashboardDropdown from "../DashboardDropdown";
import HeatmapTable from "Samarth Module/components/SamarthAi/Graphs/Heatmap";
import SingleKPI from "./SingleKPI";
import SemiCircleChart from "./SemiCircleChart";
import ActionMenu from "../ActionMenu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";

import {
  PIE_COLORS,
  PIE_TOP_COUNT,
  PIE_OTHERS_COLOR,
  formatMultiLineGraph,
  formatComposedChart,
  formatPieChart,
  formatHeatMapData,
  groupTopPieItemsWithOthers,
  getDomain,
} from "./chartFormatters";
import { useChartExport } from "./chartExport";

// ── Static constants (outside component) ──
const DEFAULT_HEIGHT = { sm: 160, md: 200, lg: 300 };
const TOOLTIP_PROPS = {
  slotProps: {
    tooltip: {
      sx: {
        backgroundColor: "#F6F7F0 !important",
        color: "#1A2025",
        fontSize: "12px",
        p: "4px 16px !important",
        fontWeight: 400,
        borderRadius: "6px",
      },
    },
  },
};
const GRAPH_TYPE_OPTIONS = [
  { value: "barGraph", label: "Bar Graph" },
  { value: "pieChart", label: "Pie Chart" },
  { value: "multiLineGraphs", label: "Multi-Line Graph" },
  { value: "composedBarLineChart", label: "Composed Bar & Line" },
  { value: "heatMap", label: "Heat Map" },
];

const getGraphTypeLabel = (value) =>
  GRAPH_TYPE_OPTIONS.find((opt) => opt.value === value)?.label || "Graph";
const MenuStyles = {
  "& .MuiMenu-paper": {
    background: "linear-gradient(180deg, #C2E0FF -119.85%, #FFFFFF 100%)",
    border: "1px solid #C2E0FF",
    borderRadius: "10px",
    p: "2px",
    "& .MuiMenu-list": {
      p: 0,
      "& .MuiMenuItem-root": {
        p: "12px",
        borderBottom: "1px solid #D9E1E7",
        fontSize: "14px",
        lineHeight: "140%",
        color: "#667F93",
        borderRadius: "4px",
        "&:hover": {
          background: "#fff",
        },
        "&:last-child": {
          borderBottom: 0,
        },
        "& img": {
          mr: 1,
        },
      },
    },
  },
};

// ── Pure helper ──
const truncateTitle = (text, wordLimit = 7, view) => {
  if (!text) return "";

  const words = text.trim().split(/\s+/);

  // dashboards view → show only first 2 words
  if (view === "dashboards") {
    return words.length <= 3
      ? words.join(" ")
      : words.slice(0, 3).join(" ") + "...";
  }

  // default logic
  return wordLimit > 6
    ? text
    : words.length <= 3
      ? words.slice(0, 3).join(" ")
      : words.slice(0, 3).join(" ") + "...";
};
// ── Memoized sub-components ──
const CustomIconBtn = memo(({ src, onClick, sx = {}, ...rest }) =>
  src ? (
    <Box className={styles.iconBG} sx={sx} onClick={onClick} {...rest}>
      <Box
        component="img"
        src={src}
        sx={{ height: { xs: "12px", md: "16px", lg: "24px" }, width: "auto" }}
      />
    </Box>
  ) : null,
);

const CustomSkeletonRows = memo(({ columns, count = 5 }) => (
  <>
    {Array.from({ length: count }).map((_, rowIndex) => (
      <Box
        key={rowIndex}
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(140px, 180px) repeat(${columns.length}, minmax(120px, 1fr))`,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRight: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Skeleton width={100} height={20} />
        </Box>
        {columns.map((_, colIndex) => (
          <Box
            key={colIndex}
            sx={{
              p: 1,
              borderRight: "1px solid #E0E0E0",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <Skeleton variant="rounded" height={36} />
          </Box>
        ))}
      </Box>
    ))}
  </>
));

const PieChartSkeleton = memo(
  ({ chartData = [], outerRadius = 100, innerRadius = 60 }) => {
    const itemCount = chartData.length || 5;
    return (
      <Box
        sx={{
          width: "100%",
          height: DEFAULT_HEIGHT,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: outerRadius * 2,
            height: outerRadius * 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Skeleton
            variant="circular"
            width={outerRadius * 2}
            height={outerRadius * 2}
          />
          <Box
            sx={{
              position: "absolute",
              width: innerRadius * 2,
              height: innerRadius * 2,
              bgcolor: "background.default",
              borderRadius: "50%",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {Array.from({ length: itemCount }).map((_, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="circular" width={10} height={10} />
              <Skeleton variant="text" width={70} height={20} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  },
);

const ChartSkeleton = memo(() => (
  <Box>
    <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1 }} />
    <Stack direction="row" spacing={3} justifyContent="center" mt={2}>
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} width={100} height={20} />
      ))}
    </Stack>
  </Box>
));

const SemiPieChartSkeleton = memo(
  ({ itemCount = 6, outerRadius = 130, innerRadius = 75 }) => (
    <Box
      sx={{
        width: "100%",
        minHeight: DEFAULT_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: { xs: 2, md: 3, lg: 4 },
        px: 2,
        py: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: 360,
        }}
      >
        {Array.from({ length: itemCount }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #E8ECF0",
              borderRadius: "10px",
              px: 1.5,
              py: 1,
              gap: 1,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
            >
              <Skeleton variant="circular" width={10} height={10} />
              <Skeleton variant="text" width="65%" height={16} />
            </Box>
            <Skeleton variant="text" width={48} height={18} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: "relative",
          width: outerRadius * 2,
          height: outerRadius,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Skeleton
          variant="circular"
          width={outerRadius * 2}
          height={outerRadius * 2}
          sx={{ position: "absolute", top: 0, left: 0 }}
        />
        <Box
          sx={{
            position: "absolute",
            top: outerRadius - innerRadius,
            left: outerRadius - innerRadius,
            width: innerRadius * 2,
            height: innerRadius * 2,
            borderRadius: "50%",
            bgcolor: "#fff",
          }}
        />
      </Box>
    </Box>
  ),
);

const NoGraphData = memo(({ height = DEFAULT_HEIGHT }) => (
  <Box
    sx={{
      height,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      color: "#94A3B8",
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#4D5F6E" }}>
      No Data Available
    </Typography>
  </Box>
));

const CustomChartOrTable = memo(
  ({
    loading,
    activeView,
    chartData,
    customrenderChart,
    headerConfig,
    rows,
    colors,
    maxHeight,
    graphType,
    variant,
    views,
  }) => {
    if (loading && graphType === "barGraph" && variant === "horizontal") {
      return (
        <Box>
          {chartData?.map((_, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="rounded" height={26} />
            </Box>
          ))}
        </Box>
      );
    }

    if (loading && graphType === "barGraph" && variant === "vertical") {
      return (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                px: 1,
              }}
            >
              <Skeleton
                variant="rounded"
                width={40}
                height={100 + i * 15}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton variant="text" width={40} height={20} />
            </Box>
          ))}
        </Box>
      );
    }

    if (loading && graphType === "pieChart" && variant === "semi") {
      return <SemiPieChartSkeleton itemCount={chartData?.length || 6} />;
    }

    if (loading && graphType === "pieChart") {
      return <PieChartSkeleton chartData={chartData} />;
    }

    if (loading && graphType === "heatMap") {
      return <CustomSkeletonRows columns={headerConfig} />;
    }

    if (loading && graphType === "multiLineGraphs") {
      return <ChartSkeleton />;
    }

    if (activeView === "graph") {
      return customrenderChart?.();
    }

    const firstRowCells = headerConfig?.[0]?.cells;

    const colCount = firstRowCells
      ? firstRowCells.length
      : (headerConfig?.length ?? 0);

    const isKPI =
      rows?.length === 1 && rows?.[0] && colCount >= 1 && colCount <= 2;

    if (isKPI) {
      const columns = firstRowCells || headerConfig;

      const items = columns.map((col, idx) => {
        const valueKey = col?.accessor || col?.key || col?.label;

        let value = rows[0]?.[valueKey];

        if (value === undefined) {
          value = rows[0]?.cells?.[idx]?.value;
        }

        return {
          title: col?.label || col?.header || "KPI",
          value: value ?? "-",
          year: rows[0]?.year ?? "",
        };
      });

      return <SingleKPI items={items} loading={loading} view={views} />;
    }

    return (
      <OrderOverviewTable
        headerConfig={headerConfig}
        rows={rows}
        colors={colors}
        loading={loading}
        maxHeight={maxHeight}
      />
    );
  },
);

// ── Main Component ──
const OverallView = ({
  data = [],
  title = "",
  lastRefreshed = "",
  icons = {},
  showViewOptions = true,
  showDashboardDropdown = true,
  showRefresh = true,
  showChat = true,
  ShowDragDrop = true,
  showExpand = true,
  showDelete = true,
  onDelete,
  headerConfig = [],
  rows = [],
  colors = {},
  refreshTrigger,
  onGraphClick,
  onGraphTypeChange,
  msg,
  messageId,
  loading,
  onRefresh,
  stretch = false,
  wordLimit,
  showExport = true,
  exportId,
  views,
  conversationId,
  tableStatus,
}) => {
  const scopeId = exportId ?? messageId;
  const [chartData] = useState(data);
  const [openExpand, setOpenExpand] = useState(false);
  const msgArray = useMemo(() => (Array.isArray(msg) ? msg : [msg]), [msg]);

  const firstRowCell = headerConfig?.[0]?.cells;

  const colCount = firstRowCell
    ? firstRowCell.length
    : (headerConfig?.length ?? 0);

  const isKPI =
    rows?.length === 1 && rows?.[0] && colCount >= 1 && colCount <= 2;
  const initialActiveView = useMemo(
    () =>
      msg?.viewMode === "graph" || msgArray[0]?.viewMode === "graph"
        ? "graph"
        : "table",
    [msg?.viewMode, msgArray],
  );
  const [graphType, setGraphType] = useState(
    msg?.graph?.type || msg?.[0]?.type,
  );
  const [activeView, setActiveView] = useState(initialActiveView);

  useEffect(() => {
    if (msg?.viewMode === "graph" || msg?.[0]?.viewMode === "graph") {
      setGraphType(msg?.graph?.type || msg?.[0]?.type);
    }
  }, [msg?.graph?.type, msg?.viewMode, msg]);

  const viewOptions = useMemo(
    () => [
      { key: "table", icon: icons.table },
      { key: "graph", icon: icons.graph },
    ],
    [icons.table, icons.graph],
  );

  const getIconStyle = useCallback(
    (isActive) => ({
      height: { sm: "20px", md: "24px", lg: "28px" },
      width: { sm: "20px", md: "24px", lg: "28px" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",
      cursor: "pointer",
      backgroundColor: isActive ? "#fff" : "transparent",
      transition: "all 0.3s ease",
      border: isActive ? "1px solid #D9E1E7" : "none",
      "&:hover": { backgroundColor: isActive ? "#fff" : "#C2E0FF" },
    }),
    [],
  );

  const handleRefresh = useCallback(() => onRefresh?.(), [onRefresh]);

  useEffect(() => {
    if (refreshTrigger) handleRefresh();
  }, [refreshTrigger, handleRefresh]);

  // ── Export (state, handlers, offscreen render) lives in useChartExport ──
  const {
    exportAnchor,
    handleExportOpen,
    handleExportClose,
    menuItems,
    contentRef,
    expandedContentRef,
    exportContainerNode,
  } = useChartExport({
    msg,
    headerConfig,
    rows,
    title,
    lastRefreshed,
    activeView,
    graphType,
    openExpand,
    scopeId,
  });

  // ── Chart renderer ──
  const CustomrenderChart = useCallback(
    (height = DEFAULT_HEIGHT) => {
      const graphData = msg?.graph?.data || msg?.[0]?.data || [];
      const xKey = msg?.graph?.xKey || msg?.[0]?.xKey || [];
      const yKey = msg?.graph?.yKey || msg?.[0]?.yKey || [];
      const graphStyles = msg?.graph?.styles || msg?.[0]?.styles || {};
      const noGraphData = msg?.noGraphData || msg?.[0]?.noGraphData;
      const loadingGraph = msg?.loadingGraph || msg?.[0]?.loadingGraph;
      const graphFetched = msg?.graphFetched || msg?.[0]?.graphFetched;
      const thresholdProps = {
        threshold: graphStyles.threshold,
        thresholdColor: graphStyles.thresholdColor,
        aboveThresholdColor: graphStyles.aboveThresholdColor,
        belowThresholdColor: graphStyles.belowThresholdColor,
      };

      if (loadingGraph) {
        return (
          <Box
            sx={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        );
      }

      if (noGraphData || (graphFetched && !graphData.length)) {
        return <NoGraphData height={height} />;
      }

      if (!graphData.length) {
        return <NoGraphData height={height} />;
      }

      switch (graphType) {
        case "heatMap": {
          const { columns, data, rowHeaderTitle } = formatHeatMapData(
            msg?.graph || msg?.[0],
          );
          return (
            <HeatmapTable
              title={msg?.graph?.graph_title}
              lastRefreshed={msg?.graph?.lastRefreshed}
              columns={columns}
              data={data}
              rowHeaderTitle={rowHeaderTitle}
            />
          );
        }
        case "pieChart": {
          const graphData = msg?.graph || msg?.[0];
          const isSemi = graphData?.variant === "semi";

          const formattedData = formatPieChart(graphData);
          const groupedData = groupTopPieItemsWithOthers(formattedData);
          const hasOthers = groupedData.some((d) => d?.isOthers);
          const groupedColors = hasOthers
            ? [...PIE_COLORS.slice(0, PIE_TOP_COUNT), PIE_OTHERS_COLOR]
            : PIE_COLORS;

          if (isSemi) {
            return (
              <SemiCircleChart
                title={graphData?.title}
                lastRefreshed={graphData?.lastRefreshed}
                data={groupedData}
                colors={groupedColors}
              />
            );
          }

          return (
            <PieChart
              data={groupedData}
              colors={groupedColors}
              innerRadius={70}
              outerRadius={110}
              threshold={thresholdProps.threshold}
              thresholdColor={thresholdProps.thresholdColor}
              belowThresholdColor={thresholdProps.belowThresholdColor}
            />
          );
        }
        case "composedBarLineChart": {
          const {
            chartData: cData,
            barKey,
            lineKey,
            areaKey,
            xKey: cXKey,
          } = formatComposedChart(msg?.graph || msg?.[0]);
          if (!cData.length) return <NoGraphData height={height} />;
          return (
            <CustomComposedChart
              data={cData}
              barKey={barKey}
              lineKey={lineKey}
              areaKey={areaKey}
              xKey={cXKey}
              loading={loading}
            />
          );
        }
        case "multiLineGraphs": {
          const {
            chartData: mlData,
            lines,
            legendItems,
            yTicks,
            xKey: mlXKey,
          } = formatMultiLineGraph(msg?.graph || msg?.[0]);
          if (!mlData.length) return <NoGraphData height={height} />;
          const domain = getDomain(mlData, lines);
          return (
            <MultiLineChart
              chartData={mlData}
              lines={lines}
              legendItems={legendItems}
              yTicks={yTicks}
              domain={domain}
              xKey={mlXKey}
              {...thresholdProps}
            />
          );
        }
        case "barGraph": {
          if (
            msg?.graph?.variant === "vertical" ||
            msg?.[0]?.variant === "vertical"
          ) {
            return (
              <VerticalBarChart
                data={graphData}
                name={xKey}
                value={yKey}
                refreshTrigger={refreshTrigger}
                {...thresholdProps}
              />
            );
          }
          if (
            msg?.graph?.variant === "horizontal" ||
            msg?.[0]?.variant === "horizontal"
          ) {
            return (
              <HorizontalBarChart
                data={graphData}
                name={xKey}
                value={yKey}
                onRefresh={onRefresh}
                {...thresholdProps}
              />
            );
          }
          return null;
        }
        default:
          return null;
      }
    },
    [msg, graphType, refreshTrigger, onRefresh, loading],
  );

  // ── Derived values ──
  const fullTitle = msg?.graph?.title || title;
  const displayTitle = truncateTitle(fullTitle, wordLimit, views);
  const cellLength = headerConfig?.[0]?.cells?.length || 0;
  const GraphTypeDropdown = memo(({ value, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    return (
      <>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            px: 1.25,
            py: 0.5,
            borderRadius: "8px",
            border: "1px solid #D9E1E7",
            background: "#fff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: "#1A2025",
            minWidth: 120,
            height: 38,
            justifyContent: "space-between",
            "&:hover": { borderColor: "#0066CC" },
          }}
        >
          <span>{getGraphTypeLabel(value)}</span>
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#4D5F6E" }} />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: 180,
                borderRadius: "10px",
                border: "1px solid #D9E1E7",
                boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
              },
            },
          }}
        >
          {GRAPH_TYPE_OPTIONS.map((opt) => {
            const isActive = opt.value === value;
            return (
              <MenuItem
                key={opt.value}
                onClick={() => {
                  onChange?.(opt.value);
                  setAnchorEl(null);
                }}
                sx={{
                  fontSize: 13,
                  color: "#1A2025",
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <span>{opt.label}</span>
                {isActive && (
                  <CheckIcon sx={{ fontSize: 16, color: "#0066CC" }} />
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  });
  // ── Controls ──
  const renderControls = useCallback(
    (expandIcon, expandAction) => (
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: "20px" }}
        justifyContent="space-between"
      >
        <Box display="flex" gap={1} alignItems="center">
          {showDelete && (
            <Tooltip title="Delete" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn
                  src={icons.delete}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                />
              </span>
            </Tooltip>
          )}
          <Box
            display="flex"
            flexDirection={cellLength === 1 ? "column" : "row"}
            alignItems={cellLength === 1 ? "flex-start" : "center"}
            gap={cellLength === 1 ? "0px" : "4px"}
          >
            <Tooltip {...TOOLTIP_PROPS} title={fullTitle} placement="top">
              <Typography className={styles.title} noWrap>
                {displayTitle}
              </Typography>
            </Tooltip>
            {showRefresh && (
              <>
                {cellLength !== 1 && (
                  <Box
                    sx={{ borderRight: "1px solid #B3C5D4", height: "20px" }}
                  />
                )}
                <Tooltip
                  {...TOOLTIP_PROPS}
                  title={lastRefreshed}
                  placement="top"
                >
                  <Typography
                    className={styles.time}
                    sx={{ fontSize: { md: "10px", lg: "12px" } }}
                    noWrap
                  >
                    {truncateTitle(lastRefreshed, wordLimit, views)}
                  </Typography>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {!isKPI && showViewOptions && (
            <Box className={styles.viewToggle}>
              {viewOptions.map(
                ({ key, icon }) =>
                  icon && (
                    <Tooltip
                      key={key}
                      title={key === "table" ? "Table" : "Graph"}
                      placement="top"
                      {...TOOLTIP_PROPS}
                    >
                      <Box
                        onClick={() => {
                          setActiveView(key);
                          if (
                            key === "graph" &&
                            !msg?.graphFetched &&
                            !msg?.graph
                          ) {
                            onGraphClick?.();
                          }
                        }}
                        sx={getIconStyle(activeView === key)}
                      >
                        <Box
                          component="img"
                          src={icon}
                          sx={{
                            width: { xs: 12, md: 16, lg: 20 },
                            height: { xs: 12, md: 16, lg: 20 },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  ),
              )}
            </Box>
          )}
          {window.location.hostname !== "samarth-hul.azurewebsites.net" &&
            showViewOptions &&
            activeView === "graph" && (
              <GraphTypeDropdown
                value={graphType || msg?.graph?.type || msg?.[0]?.type}
                onChange={(newType) => {
                  setGraphType(newType);
                  onGraphTypeChange?.(newType);
                }}
              />
            )}
          {showDashboardDropdown && (
            <DashboardDropdown
              messageId={messageId}
              viewtype={activeView}
              columnCount={headerConfig?.[0]?.cells?.length || 0}
              graphData={msg?.graph || msg?.[0]}
              conversationId={conversationId}
            />
          )}
          {showRefresh && (
            <Tooltip title="Refresh" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn src={icons.refresh} onClick={handleRefresh} />
              </span>
            </Tooltip>
          )}
          {showChat && (
            <Tooltip title="Chat" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn src={icons.chat} />
              </span>
            </Tooltip>
          )}
          {showExport && (
            <Tooltip title="Export" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn src={icons.export} onClick={handleExportOpen} />
              </span>
            </Tooltip>
          )}
          {showExpand && !isKPI && (
            <Tooltip title="Expand" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn src={expandIcon} onClick={expandAction} />
              </span>
            </Tooltip>
          )}
          {ShowDragDrop && (
            <Tooltip title="Drag to reorder" placement="top" {...TOOLTIP_PROPS}>
              <span>
                <CustomIconBtn
                  src={icons.DragDrop}
                  sx={{ cursor: "grab" }}
                  onClick={(e) => e.stopPropagation()}
                  data-drag-handle
                />
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    ),
    [
      fullTitle,
      displayTitle,
      cellLength,
      showRefresh,
      showViewOptions,
      showChat,
      showExpand,
      ShowDragDrop,
      showDelete,
      showDashboardDropdown,
      showExport,
      viewOptions,
      activeView,
      icons,
      getIconStyle,
      handleRefresh,
      handleExportOpen,
      onDelete,
      onGraphClick,
      msg,
      lastRefreshed,
      messageId,
      headerConfig,
      isKPI,
      wordLimit,
    ],
  );

  // ── No-data check ──
  const hasRows = rows?.length > 0;
  const hasGraph = msgArray.some((m) => {
    if (!m) return false;
    if (Array.isArray(m.data) && m.data.length > 0) return true;
    if (Array.isArray(m.raw?.data) && m.raw.data.length > 0) return true;
    if (Array.isArray(m.raw) && m.raw.length > 0) return true;
    return !!m.type;
  });
  const isNoData = !loading && !hasRows && !hasGraph;

  if (isNoData) {
    return (
      <Box sx={{ py: 2, px: 2, fontSize: "14px", color: "#667F93" }}>
        No data available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
        width: "100%",
        overflow: "hidden",
        ...(stretch && {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      {(msg?.tableStatus || tableStatus) && (
        <Box
          sx={{
            p: { sm: "16px", md: "20px", lg: "20px" },
            ...(stretch && {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }),
            border: "1px solid #d9e1e7",
            borderRadius: "16px",
          }}
        >
          {renderControls(icons.expand, () => setOpenExpand(true))}

          <Box
            ref={contentRef}
            data-export-id={scopeId}
            data-export-scope="default"
            className={activeView === "table" ? "" : styles.graphBG}
            sx={stretch ? { flex: 1, minHeight: 0 } : {}}
          >
            {isNoData ? (
              <Box sx={{ py: 2, px: 2, fontSize: "14px", color: "#667F93" }}>
                No data available
              </Box>
            ) : (
              <CustomChartOrTable
                loading={loading}
                activeView={activeView}
                chartData={chartData}
                customrenderChart={() => CustomrenderChart(DEFAULT_HEIGHT)}
                headerConfig={headerConfig}
                rows={rows}
                colors={colors}
                maxHeight={openExpand ? "70vh" : DEFAULT_HEIGHT}
                graphType={graphType}
                variant={msg?.graph?.variant || msg?.[0]?.variant}
                views={views}
              />
            )}
          </Box>

          <Dialog
            open={openExpand}
            onClose={() => setOpenExpand(false)}
            fullWidth
            maxWidth="lg"
          >
            <Box sx={{ p: 3 }}>
              {renderControls(icons.expandless, () => setOpenExpand(false))}
              <Box
                ref={expandedContentRef}
                data-export-id={scopeId}
                data-export-scope="expanded"
                className={activeView === "table" ? "" : styles.graphBG}
              >
                <CustomChartOrTable
                  loading={loading}
                  activeView={activeView}
                  chartData={chartData}
                  customrenderChart={() => CustomrenderChart(500)}
                  headerConfig={headerConfig}
                  rows={rows}
                  colors={colors}
                  maxHeight="70vh"
                  graphType={graphType}
                  variant={msg?.graph?.variant || msg?.[0]?.variant}
                  views={views}
                />
              </Box>
            </Box>
          </Dialog>
          <ActionMenu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={handleExportClose}
            menuItems={menuItems}
            menuSx={MenuStyles}
          />
          {exportContainerNode}
        </Box>
      )}
    </Box>
  );
};;

export default OverallView;